/**
 * <TutorOfferDetails> — ce qu'une offre de cours dit quand on l'ouvre (organism).
 *
 * Le corps de la boîte de la liste (`ProposalsTabScreen` → `getDialogContent`) et de la page d'un
 * lien (`GroupAttributionProposalScreen`, `SubstitutionProposalScreen`) : le titre de l'offre (« 3
 * élèves - 2 séances », « Nouvel élève 5ème - Maths »), ses séances, la première séance, le
 * commentaire de l'équipe, et la phrase d'un nouvel élève (ce qui se passe s'il n'accepte pas).
 *
 * Les lignes d'un nouvel élève se distinguent par leur glyphe — le calendrier coché pour les séances
 * où il sera ajouté, le calendrier « + » pour celles qui seront créées — là où le legacy mettait un
 * emoji (📅, ➕) en tête de phrase. L'ordre des lignes est celui de l'adaptateur : la liste et la page
 * ne les rangent pas pareil.
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { type UITutorOfferDetail, type UITutorOfferLine } from "../../../ports/primary/ui/types/tutorDispoOffres"
import { StyleSheet } from "react-native-unistyles"

const LINE_ICON: Record<NonNullable<UITutorOfferLine["kind"]>, IconName> = {
    existing: "calendar-check",
    new: "calendar-plus",
}

/** Une ligne de créneaux, avec son glyphe quand elle en a un. Partagée avec la carte de la liste. */
export function TutorOfferLineText(props: { line: UITutorOfferLine; muted?: boolean; size?: "text-sm" | "text-md" }) {
    const { line, muted = false, size = "text-md" } = props
    const color = muted ? "text-muted" : "text-primary"
    if (line.kind == null) {
        return (
            <Text size={size} color={color}>
                {line.text}
            </Text>
        )
    }
    return (
        <Row gap="2" align="start">
            <Icon name={LINE_ICON[line.kind]} size={16} color={muted ? "text-muted" : "role"} />
            <Text size={size} color={color} style={styles.flex}>
                {line.text}
            </Text>
        </Row>
    )
}

export type TutorOfferDetailsProps = {
    detail: UITutorOfferDetail
    testID?: string
}

export function TutorOfferDetails(props: TutorOfferDetailsProps) {
    const { detail, testID } = props

    return (
        <Stack gap="3" testID={testID}>
            {detail.heading != null && detail.heading !== "" && (
                <Text size="text-lg" weight="bold">
                    {detail.heading}
                </Text>
            )}
            <Stack gap="1">
                {detail.lines.map((line) => (
                    <TutorOfferLineText key={line.text} line={line} />
                ))}
            </Stack>
            {detail.startLabel != null && detail.startLabel !== "" && (
                <Text size="text-md" weight="semibold">
                    {detail.startLabel}
                </Text>
            )}
            {detail.comment != null && detail.comment !== "" && (
                <Text size="text-md" color="text-muted">
                    {`Commentaire : ${detail.comment}`}
                </Text>
            )}
            {detail.note != null && detail.note !== "" && (
                <Banner message={detail.note} color="info" icon="info-circle" tinted align="start" />
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    flex: { flex: 1, flexShrink: 1 },
}))
